import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Settings2 } from "lucide-react";

const FIELDS = [
  { key: "eur_ron_rate", label: "Curs RON → EUR (1 EUR = X RON)", hint: "Salariile (RON) și deconturile în RON se convertesc în EUR la acest curs.", step: "0.0001" },
  { key: "employer_contrib_pct", label: "Contribuții angajator (%)", hint: "CAM 2,25% implicit — se adaugă peste salariul brut pentru costul real al firmei.", step: "0.01" },
  { key: "indirect_pct_default", label: "Cheltuieli indirecte implicite (%)", hint: "Procent din costul manoperei, aplicat proiectelor fără procent propriu.", step: "0.1" },
  { key: "hours_per_day", label: "Ore pe zi lucrătoare", hint: "O lună la 100% alocare = zile lucrătoare (fără weekend și sărbători legale) × aceste ore.", step: "0.5" },
];

export function PnlSettingsDialog({ open, onClose, settings, canEdit, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && settings) setForm({ ...settings }); }, [open, settings]);

  async function save() {
    setBusy(true);
    try {
      const payload = Object.fromEntries(FIELDS.map((f) => [f.key, parseFloat(form[f.key])]));
      const { data } = await api.put("/pnl/settings", payload);
      toast.success("Setări P&L salvate");
      onSaved?.(data); onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="pnl-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold"><Settings2 className="h-5 w-5 text-[#3d68b1]" /> Setări Profit & Loss</DialogTitle>
          <DialogDescription>Parametrii globali folosiți la calculul planului, costurilor și marjei.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-[12px] font-medium text-slate-700">{f.label}</label>
              <input type="number" step={f.step} data-testid={`pnl-setting-${f.key}`} disabled={!canEdit}
                value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className="pio-input w-full font-mono mt-1 disabled:opacity-60" />
              <div className="text-[11px] text-slate-400 mt-1">{f.hint}</div>
            </div>
          ))}
          <div className="surface-inset p-3 text-[11px] text-slate-500">
            Cost intern orar = (salariu brut × (1 + contribuții)) ÷ orele lunii ÷ curs. Dacă angajatul are completat „Tarif intern orar (EUR/oră)" în fișă, acela are prioritate.
          </div>
        </div>
        {canEdit && (
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-800">Renunță</button>
            <button data-testid="pnl-settings-save" onClick={save} disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-[#3d68b1] text-white disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvează
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
